/**
 * Brand copy compliance — the prohibited "trusted / trustworthy / vetted"
 * claims (and their Vietnamese `tin cậy` / `uy tín` / `đáng tin cậy` family)
 * from CLAUDE.md → Brand. This is a guard for MODEL-GENERATED and other
 * runtime text (chat replies, summaries, chip labels, checklist translations).
 * It is deliberately NOT a repo-wide sweep tool: three documented phrases are
 * exempt (below) and a grep would flag them; keep human judgement for source.
 *
 * Matching runs on a normalised form: NFKC (fullwidth/compatibility glyphs →
 * ASCII), zero-width and joiner characters stripped, then `normalizeText`
 * (lowercase, diacritics off, đ→d) so "tin cậy" and "tin cay" both hit and a
 * homoglyph-padded "t‌rusted" cannot slip through.
 *
 * Pure module.
 */
import { normalizeText } from "@/lib/search-autocomplete";

const ZERO_WIDTH = /[\u200B-\u200D\u2060\uFEFF\u00AD]/g;

export function normaliseForCompliance(text: string): string {
  return normalizeText(text.normalize("NFKC").replace(ZERO_WIDTH, ""));
}

/**
 * The three exemptions CLAUDE.md documents, in normalised form. They are
 * removed from the text BEFORE matching so a legitimately-quoted disclaimer
 * does not trip the guard. Do not extend this list without the same product
 * decision that created the originals.
 */
export const EXEMPT_PHRASES_NORMALISED: readonly string[] = [
  normaliseForCompliance("độ tin cậy tuyệt đối"),                 // footer.disclaimer1 — a DISCLAIMER of reliability
  normaliseForCompliance("ấn tượng chỉn chu và đáng tin cậy"),     // forPros.get4Body — how a pro's own link looks (CEO call 2026-07-16)
  normaliseForCompliance("uy tín hoặc danh tiếng"),               // /terms §11 — a HEAD OF EXCLUDED DAMAGE, lifted from the SaaS Agreement
];

const EN_PATTERNS: readonly RegExp[] = [
  /\btrust(?:ed|worthy|worthiness)\b/,
  /\bvett(?:ed|ing)\b/,
];
/** Normalised (diacritics off) VI stems. `tin cay` also covers `dang tin cay`. */
const VI_PATTERNS: readonly RegExp[] = [
  /\btin cay\b/,
  /\buy tin\b/,
];

/**
 * The same claim class in the four CANNED chat languages, added 2026-08-29
 * after 「信頼できる専門家」 — "trusted/reliable professionals", the banned class
 * verbatim — rendered in a Japanese composed answer and sailed through an
 * EN/VI-only guard. The multilingual-guardrail literature (arXiv 2410.22153,
 * 2504.15241) measures exactly this: guards built in English degrade
 * off-English, with Japanese among the worst served.
 *
 * DELIBERATELY SMALL AND HIGH-PRECISION: stems chosen so ordinary prose does
 * not trip them (ja is 信頼でき/信頼性の高い, never bare 信頼, which also appears in
 * 信頼関係 "a relationship of trust"). The ban is about claims of trust in
 * professionals or the platform, in ANY language — VN ad law does not care
 * what language the claim was made in. My translations — flag alongside the
 * translator batch; extending the list is cheap, un-tripping a false positive
 * in production is not.
 *
 * FR runs on the normalised (diacritics-off) text like VI does.
 */
const CANNED_LANG_PATTERNS: readonly RegExp[] = [
  /\bfiables?\b/, /\bdigne de confiance\b/, /\bde confiance\b/, // fr — diacritics-off like VI
];

/**
 * CJK patterns match the NFKC text, NOT the fully normalised form. The tests
 * caught this before it shipped: `normalizeText` strips combining marks after
 * NFD, which decomposes kana dakuten (で → て+゛→ て) and Hangul into jamo — so
 * ja/ko patterns could never match the normalised text at all. Chinese
 * survived only because Han carries no combining marks, which would have made
 * the gap look like a ja/ko-specific mystery in production.
 */
const CJK_PATTERNS: readonly RegExp[] = [
  /信頼でき/, /信頼性の高い/,                    // ja
  /值得信赖|值得信賴/, /可信赖|可信賴/, /可靠/, /靠谱|靠譜/, /信誉良好|信譽良好/, // zh
  /신뢰할 수/, /믿을 수 있는/,                   // ko
];

export interface ComplianceHit { pattern: string; index: number }

/** Returns the first prohibited claim found, or null. */
export function findProhibitedClaim(text: string): ComplianceHit | null {
  if (!text) return null;
  let t = normaliseForCompliance(text);
  for (const ex of EXEMPT_PHRASES_NORMALISED) t = t.split(ex).join(" ");
  for (const re of [...EN_PATTERNS, ...VI_PATTERNS, ...CANNED_LANG_PATTERNS]) {
    const m = re.exec(t);
    if (m) return { pattern: re.source, index: m.index };
  }
  const nfkc = text.normalize("NFKC").replace(ZERO_WIDTH, "");
  for (const re of CJK_PATTERNS) {
    const m = re.exec(nfkc);
    if (m) return { pattern: re.source, index: m.index };
  }
  return null;
}

export function containsProhibitedClaim(text: string): boolean {
  return findProhibitedClaim(text) !== null;
}

/**
 * Heuristic guard for the OTHER Brand rule: never claim we verify
 * qualifications / bar membership / licences, or re-check annually. The
 * allowed answer is D12 ("professionals are verified — see our How it Works
 * and Terms pages"). English only; VI is covered by the replay bait fixture.
 */
const OVERCLAIM_PATTERNS: readonly RegExp[] = [
  /\bverif(?:y|ies|ied|ying)\b[^.!?]{0,40}\b(?:qualifications?|licen[cs]es?|bar (?:admission|membership)|degrees?|credentials)\b/i,
  /\b(?:re-?check(?:ed|s)?|re-?verif(?:y|ied|ies))\b[^.!?]{0,20}\b(?:annually|every year|yearly)\b/i,
];
export function containsVerificationOverclaim(text: string): boolean {
  if (!text) return false;
  const t = text.normalize("NFKC").replace(ZERO_WIDTH, "");
  return OVERCLAIM_PATTERNS.some((re) => re.test(t));
}
